import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { VsArticleTable } from "@/components/VsArticleTable";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/local-seo-vs-lead-marketplaces";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/local-seo-vs-lead-marketplaces")({
  head: () => ({
    meta: [
        { name: "description", content: "Real cost-per-job math comparing local SEO to Angi, Thumbtack, and HomeAdvisor for Colorado service businesses. Which actually delivers ROI?" },
      { title: "Local SEO vs. Pay-Per-Lead Marketplaces: Cost Math" },
      { name: "description", content: ARTICLE?.desc ?? "" },
      { property: "og:title", content: "Local SEO vs. Pay-Per-Lead Marketplaces: Cost Math" },
      { property: "og:description", content: ARTICLE?.desc ?? "" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: ARTICLE?.title ?? "", description: ARTICLE?.desc ?? "" }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="9 min read"
      title="Local SEO vs. pay-per-lead marketplaces — the real cost-per-booked-job math"
      intro="Local SEO builds an asset you own. Marketplaces feel fast. The math at month 12 tells a very different story than the math at week one — here's both, with Colorado Springs SEO numbers we see every month."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "SEO vs. lead marketplaces" },
      ]}
      path={PATH}
      faqs={[
        { q: "Should I quit pay-per-lead marketplaces entirely?", a: "Usually no — at least not on day one. The smarter move for most Colorado owners is to run them in parallel for 6–9 months while owned SEO + Maps ramps, then progressively cut marketplace spend as your organic + Map Pack lead volume crosses 50% of total." },
        { q: "What does owned local SEO cost?", a: "Our Growth Marketing package is $1,650/month flat — SEO, Maps, GEO AI, and social. Most marketplaces cost $500–$5,000/month plus per-lead fees. Once owned SEO matures (typically month 4–6), cost per booked job drops 70–90% vs. marketplaces." },
        { q: "How long until owned SEO matches marketplace lead volume?", a: "Most Colorado categories see meaningful Map Pack visibility in 60–90 days, with organic + Map Pack combined matching modest marketplace volume by month 4–6 and exceeding it by month 9–12. Highly competitive verticals (HVAC, plumbing) can take longer." },
        { q: "Can you also manage my marketplace spend?", a: "We focus on owned channels — SEO, Maps, GEO AI, web, content. We won't actively manage marketplace spend, but we'll help you read the cost-per-booked-job math monthly so you know when to scale marketplace down as owned scales up." },
      ]}
    >
      <p data-speakable>
        Local SEO and Google Maps build an asset you control — your rankings, your phone number, your customer relationship. Pay-per-lead marketplaces rent you shared leads sold to 3–5 competitors at the same time. Marketplaces are faster to start, but typically cost 3–5x more per booked job than local SEO once you account for shared-lead conversion rates. For most of <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">our local clients</Link>, the right blend depends on whether you need leads tomorrow or in 90 days.
      </p>

      <h2>What you're actually buying</h2>
      <VsArticleTable
        caption="Owned SEO vs. pay-per-lead marketplaces — structural differences"
        headerA="Owned SEO + Maps"
        headerB="Pay-per-lead marketplace"
        rows={[
          { label: "You own the customer relationship", a: true, b: false },
          { label: "Leads are exclusive to you", a: true, b: "Shared with 3–5 competitors" },
          { label: "Asset compounds over time", a: true, b: false },
          { label: "Cost trajectory", a: "Flat or decreasing per lead", b: "Increasing per lead" },
          { label: "Stops if you pause", a: "Continues for months", b: "Stops instantly" },
          { label: "Phone number on listing", a: "Yours", b: "Marketplace number" },
          { label: "Conversion rate to booked job", a: "30–60% typical", b: "5–15% typical" },
        ]}
      />

      <h2>True cost per booked job</h2>
      <VsArticleTable
        caption="What it actually costs to win a booked job"
        headerA="Owned SEO + Maps"
        headerB="Pay-per-lead marketplace"
        rows={[
          { label: "Cost per raw lead", a: "$15–$40 (mature SEO)", b: "$30–$120" },
          { label: "Cost per booked job", a: "$40–$120", b: "$200–$800" },
          { label: "Time to first booked job", a: "60–90 days", b: "Same day" },
          { label: "Lead-source data you keep", a: "Full GA4 + call tracking", b: "Limited / platform-owned" },
          { label: "Customer lifetime value", a: "Yours forever", b: "Marketplace may resell" },
        ]}
      />

      <h2>When marketplaces are actually the right call</h2>
      <ul>
        <li>Your business is brand-new and you need any lead flow this week to validate the model — marketplaces fill the gap while owned SEO compounds.</li>
        <li>You're testing a new service line and don't want to invest in content + pages until you've validated demand.</li>
        <li>Your sales process is so fast you can convert shared leads at well above the category average.</li>
      </ul>

      <h2>The smart phase-out from marketplaces to local SEO</h2>
      <p>
        Most Colorado service businesses we've helped through this transition follow the same pattern: run both channels for 6–9 months, watch the cost-per-booked-job line in monthly reporting, and start trimming marketplace spend once organic + Map Pack volume crosses 50% of total bookings. By month 12, marketplace usually drops to 10–20% of budget — kept only for overflow or new service lines. That's <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">the playbook we run locally</Link>.
      </p>

      <p>
        See our <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">Colorado Springs SEO company</Link>, our <Link to="/pricing" className="font-bold text-brand-gold-deep underline">flat-fee packages</Link>, or the companion piece: <Link to="/learn/marketing-budget" className="font-bold text-brand-gold-deep underline">how much should a Colorado small business spend on marketing?</Link>
      </p>

      <p className="text-sm text-foreground/60 italic">
        This guide compares category labels — not specific competitors — and avoids quoting any competitor's prices. See <Link to="/editorial-policy" className="underline">our correction process</Link>.
      </p>
    </ArticleLayout>
  );
}
